<script setup lang="ts">
import { ref, computed } from 'vue';
import { formatDate } from '@/utils/dateUtils';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { tourTypeList } from '@/constants';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const props = defineProps<{
  tourData: Array<any>;
  selectedTours: any;
}>();

const emits = defineEmits<{
  handleSubmit: [value: any];
  'update:tourData': [value: any];
  'update:selectedTours': [value: any];
}>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const langCapitalize = capitalize(currentLanguage);
const tourDetail = computed({
  get() {
    return props.tourData;
  },
  set(value) {
    emits('update:tourData', value);
  }
});

const selectTours = computed({
  get() {
    return props.selectedTours;
  },
  set(value) {
    emits('update:selectedTours', value);
  }
});

const isLoading = ref(false);
const columns = ref([
  { field: 'checkbox', header: t('multi_day_tour.selected_tours') },
  { field: 'depDate', header: t('multi_day_tour.date_of_departure') },
  { field: `title${capitalize(currentLanguage)}`, header: t('multi_day_tour.trip_duration') },
  { field: 'tourDurations', header: t('multi_day_tour.tour_duration') },
  { field: `tourName${capitalize(currentLanguage)}`, header: t('management.tour.tour_name') },
  { field: `tourInclusions${capitalize(currentLanguage)}`, header: t('management.tour.tour_details') },
  { field: 'type', header: t('multi_day_tour.tour_type') },
  { field: `regionsVisited${capitalize(currentLanguage)}`, header: t('multi_day_tour.citites_to_visit') },
  { field: `tourDescription${capitalize(currentLanguage)}`, header: t('multi_day_tour.tour_description') }
]);

const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);
const checkList = ref(selectTours.value?.length ? selectTours.value : []);

const submitTour = () => {
  emits('handleSubmit', checkList.value);
};

const handleCheckbox = (list: any, item: any) => {
  return list.some((tour: any) => tour.uuid === item.uuid);
};

const isCheckboxDisabled = computed(() => (item) => {
  return checkList.value.length >= 3 && !handleCheckbox(checkList.value, item);
});

const handleReplaceNameToValue = (value: any) => {
  if (value === '' || !isNaN(value)) {
    const setValue = !value ? '-1' : value;
    return tourTypeList.find((tourType: any) => tourType.value == setValue)[`name${langCapitalize}`];
  } else {
    return value;
  }
};

const checkColumnStyle = (column: any) => {
  if (column == `tourDescription${capitalize(currentLanguage)}`) {
    return '200px';
  } else if (column == 'checkbox') {
    return '40px';
  } else if (column == `regionsVisited${capitalize(currentLanguage)}`) {
    return '100px';
  } else if (column == `title${capitalize(currentLanguage)}`) {
    return '150px';
  } else {
    return '';
  }
};

const sortByDate = computed(() => {
  const cloneDetailtours = cloneDeep(tourDetail.value);
  return cloneDetailtours.sort((first, second) => new Date(first.depDate) - new Date(second.depDate));
});
</script>
<template>
  <div class="card tour-quote-search-list">
    <div class="text-sm flex align-items-center mb-2">*{{ $t('multi_day_tour.max_3_tours') }}</div>
    <div class="preview-list">
      <LoadingSpinner v-if="isLoading" />
      <DataTable
        editMode="cell"
        :value="sortByDate"
        scrollHeight="600px"
        sortMode="single"
        sortField="type"
        :sortOrder="-1"
        scrollable
        paginator
        :rows="rowsPerPageOptions[0]"
        :rowsPerPageOptions="rowsPerPageOptions"
        v-model:selection="checkList"
        showGridlines
        filterDisplay="menu"
        dataKey="uuid"
      >
        <template #empty>{{ $t('management.tour.list_tour_detail.fare_not_available') }}</template>
        <TableColumn v-for="column of columns" :headerStyle="{ 'min-width': checkColumnStyle(column.field) }" :key="column.field" :field="column.field" :selectionMode="column.field === 'checkbox' ? 'multiple' : undefined">
          <template #header>
            <div class="flex justify-content-center align-content-center">
              {{ column.header }}
            </div>
          </template>
          <template #body="slotProps">
            <div class="flex justify-content-center" v-if="column.field === 'checkbox'">
              <Checkbox :value="slotProps.data" v-model="checkList" :disabled="isCheckboxDisabled(slotProps.data)" />
            </div>
            <div class="flex justify-content-center" v-else-if="column.field === 'depDate'">
              {{ formatDate(slotProps.data[column.field], 'dd-LLL-yyyy', true, currentLanguage) }}
            </div>
            <div class="flex justify-content-center" v-else-if="column.field === 'type'">
              {{ handleReplaceNameToValue(slotProps.data[column.field]) }}
            </div>
            <div class="flex justify-content-center" v-else>
              {{ slotProps.data[column.field] }}
            </div>
          </template>
        </TableColumn>
      </DataTable>
      <div class="text-center">
        <Button id="update-tours-table" :label="$t('common.buttons.submit').toUpperCase()" :disabled="!checkList.length" @click="submitTour()"></Button>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .preview-list {
    th .p-checkbox {
      display: none;
    }
  }
}
</style>
